import React, { useEffect, useState } from "react";
import Button from "./Button";
import SelectedMedia from "../sections/admin/SelectedMediaModal/SelectedMedia";
import { classNames } from "../../helpers/classNames";

const SelectSingleImage = ({
  label,
  labelClasses = "",
  required = false,
  errorType,
  errorText,
  value = null,
  onChange = () => {},
  onRemove = () => {},
  size = "sm", // size: sm, md, lg
}) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    setSelectedImage(value ?? null);
  }, [JSON.stringify(value)]);

  const handleMediaSelect = (mediaArray) => {
    const selected = mediaArray?.[0] ?? null;
    setSelectedImage(selected);
    onChange(selected);
    setModalOpen(false);
  };

  const handleRemove = () => {
    setSelectedImage(null);
    onRemove(null);
  };

  // Reusable size classes
  const boxSize = {
    sm: "w-16",
    md: "w-32",
    lg: "w-full sm:w-44 md:w-28 lg:w-60 3xl:w-full",
  };
  const closeSize = {
    sm: "w-5 text-sm",
    md: "w-6 text-base",
    lg: "w-8 text-lg",
  };
  const uploaderIconSize = {
    sm: "text-xl",
    md: "text-2xl",
    lg: "text-3xl sm:text-xl md:text-2xl",
  };
  const uploaderTextSize = {
    sm: "text-[10px]",
    md: "text-xs",
    lg: "text-sm sm:text-xs",
  };

  const showPlaceholder = !selectedImage || !selectedImage.url;

  return (
    <div className="relative space-y-1 w-full">
      {label && (
        <div
          className={classNames(
            "text-xs font-medium text-text-disabled mb-1 font-CanelaDeck",
            labelClasses
          )}
        >
          {label}
          {required && (
            <span className="text-[8px] text-red-500 ml-1 absolute top-[-2px]">
              <i className="fa-solid fa-asterisk"></i>
            </span>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-4 pt-2">
        {showPlaceholder ? (
          <div
            className={classNames(
              "relative aspect-square overflow-hidden border-2 border-dashed border-slate-200 text-slate-400 bg-slate-50 rounded-md flex items-center justify-center flex-col gap-1 leading-none cursor-pointer",
              boxSize[size],
              uploaderIconSize[size]
            )}
            onClick={() => setModalOpen(true)}
          >
            <i className="fa-light fa-folder-open"></i>
            <div
              className={classNames(
                "font-semibold uppercase tracking-wider",
                uploaderTextSize[size]
              )}
            >
              {size === "sm" ? "Image" : "Select Image"}
            </div>
          </div>
        ) : (
          <div className="relative">
            <div
              className={classNames(
                "relative aspect-square overflow-hidden border border-slate-200 bg-white rounded-md",
                boxSize[size]
              )}
            >
              <img
                src={selectedImage.url}
                alt="Selected"
                className="object-cover w-full h-full"
              />
            </div>
            <button
              type="button"
              onClick={handleRemove}
              className={classNames(
                "aspect-square rounded-full bg-white shadow absolute -top-2.5 -right-2.5 text-red-500",
                closeSize[size]
              )}
            >
              <i className="fa-regular fa-fw fa-xmark"></i>
            </button>
          </div>
        )}
      </div>

      {errorText && (
        <div
          className={classNames(
            "text-xs mt-1",
            errorType === "error"
              ? "text-red-600"
              : errorType === "warning"
              ? "text-amber-600"
              : errorType === "success"
              ? "text-green-600"
              : "text-sky-600"
          )}
        >
          <i
            className={classNames(
              "fa-regular fa-fw text-sm mr-1",
              errorType === "error"
                ? "fa-square-exclamation"
                : errorType === "warning"
                ? "fa-triangle-exclamation"
                : errorType === "success"
                ? "fa-circle-check"
                : "fa-circle-info"
            )}
          ></i>
          {errorText}
        </div>
      )}

      <SelectedMedia
        open={modalOpen}
        close={() => setModalOpen(false)}
        onSelect={handleMediaSelect}
        selectedData={selectedImage ? [selectedImage._id] : []}
        isSingle={true}
      />
    </div>
  );
};

export default SelectSingleImage;
